/**
 * Recurring-task rules and date math. Shared by web (editor, chips), the
 * sync server (the respawn engine runs there, the one choke point every
 * completion crosses) and the Discord bot (cards, modal parsing).
 *
 * 1.0 scope: every N days/weeks/months, or a weekly weekday set. A weekday
 * set forces unit "week" and interval 1 (one normalization rule beats two
 * ambiguous ones; "every 2 weeks on Mon and Fri" is out of scope).
 *
 * All date arithmetic goes through Date.UTC so it is timezone and DST
 * independent: dateParse resolves "tomorrow" for a human in local time, but
 * recurrence math on stored "YYYY-MM-DD" strings must never shift by zone.
 */
export const MAX_RECURRENCE_INTERVAL = 365;
const UNITS = ["day", "week", "month"];
const DAY_MS = 86_400_000;
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** Display order for weekday lists (Mon-first). */
const WEEKDAY_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
/** Validate + normalize an untrusted value. Null when it is not a rule. */
export function parseRecurrence(raw) {
    if (typeof raw !== "object" || raw === null)
        return null;
    const r = raw;
    if (!UNITS.includes(r.unit))
        return null;
    const interval = Number(r.interval);
    if (!Number.isInteger(interval) || interval < 1 || interval > MAX_RECURRENCE_INTERVAL)
        return null;
    let weekdays;
    if (Array.isArray(r.weekdays) && r.weekdays.length > 0) {
        const set = new Set();
        for (const d of r.weekdays) {
            if (!Number.isInteger(d) || d < 0 || d > 6)
                return null;
            set.add(d);
        }
        if (set.size === 7)
            return { unit: "day", interval: 1 }; // every day, normalized
        weekdays = [...set].sort((a, b) => a - b);
    }
    if (weekdays) {
        if (r.unit !== "week")
            return null;
        return { unit: "week", interval: 1, weekdays };
    }
    return { unit: r.unit, interval };
}
/** Stable serialization (key order fixed) for the DB column and card hashes. */
export function serializeRecurrence(rule) {
    return JSON.stringify({
        unit: rule.unit,
        interval: rule.interval,
        ...(rule.weekdays ? { weekdays: rule.weekdays } : {}),
    });
}
function toUtc(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
}
function toIso(utcMs) {
    const d = new Date(utcMs);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
/**
 * The next occurrence for a completed instance: strictly after
 * max(today, prevDue), so completing early keeps the cadence and completing
 * an overdue task schedules from today rather than piling up the past.
 */
export function nextDueDate(rule, prevDue, today) {
    // both are "YYYY-MM-DD": lexical compare is date compare
    const base = prevDue && prevDue > today ? prevDue : today;
    const baseUtc = toUtc(base);
    if (rule.weekdays?.length) {
        const set = new Set(rule.weekdays);
        for (let i = 1; i <= 7; i++) {
            const candidate = baseUtc + i * DAY_MS;
            if (set.has(new Date(candidate).getUTCDay()))
                return toIso(candidate);
        }
    }
    switch (rule.unit) {
        case "day":
            return toIso(baseUtc + rule.interval * DAY_MS);
        case "week":
            return toIso(baseUtc + rule.interval * 7 * DAY_MS);
        case "month": {
            const d = new Date(baseUtc);
            const targetMonth = d.getUTCMonth() + rule.interval;
            const year = d.getUTCFullYear() + Math.floor(targetMonth / 12);
            const month = ((targetMonth % 12) + 12) % 12;
            // clamp to the target month's last day (Jan 31 + 1 month = Feb 28/29)
            const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
            return toIso(Date.UTC(year, month, Math.min(d.getUTCDate(), lastDay)));
        }
    }
}
/**
 * Human form, lowercase leading: "daily", "every 3 days", "weekly",
 * "every 2 weeks", "every Mon, Wed, Fri", "every weekday", "monthly",
 * "every 2 months". Callers prefix "Repeats " where they need it.
 */
export function describeRecurrence(rule) {
    if (rule.weekdays?.length) {
        const days = rule.weekdays;
        if (days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d)))
            return "every weekday";
        const names = WEEKDAY_DISPLAY_ORDER.filter((d) => days.includes(d)).map((d) => WEEKDAY_SHORT[d]);
        if (names.length === 1)
            return `every ${expandDay(days[0])}`;
        return `every ${names.join(", ")}`;
    }
    if (rule.interval === 1) {
        return rule.unit === "day" ? "daily" : rule.unit === "week" ? "weekly" : "monthly";
    }
    return `every ${rule.interval} ${rule.unit}s`;
}
function expandDay(d) {
    return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d];
}
//# sourceMappingURL=recurrence.js.map